'use client';

import { useSyncExternalStore } from 'react';
import type { FaceMeasurementRecord, PdMeasurementRecord } from './types';

let currentFaceWidthRecord: FaceMeasurementRecord | null = null;
let currentPdRecord: PdMeasurementRecord | null = null;
let currentFeedbackSubmitted = false;
let unconsumedPdRecordId: string | null = null;
const faceWidthListeners = new Set<() => void>();
const pdListeners = new Set<() => void>();
const feedbackListeners = new Set<() => void>();

function subscribeFaceWidth(listener: () => void) {
  faceWidthListeners.add(listener);
  return () => faceWidthListeners.delete(listener);
}

function subscribePd(listener: () => void) {
  pdListeners.add(listener);
  return () => pdListeners.delete(listener);
}

function subscribeFeedback(listener: () => void) {
  feedbackListeners.add(listener);
  return () => feedbackListeners.delete(listener);
}

function getFaceWidthSnapshot() {
  return currentFaceWidthRecord;
}

function getPdSnapshot() {
  return currentPdRecord;
}

function getFeedbackSnapshot() {
  return currentFeedbackSubmitted;
}

function getServerFeedbackSnapshot() {
  return false;
}

function getServerSnapshot() {
  return null;
}

export function saveFaceMeasurementSession(record: FaceMeasurementRecord) {
  currentFaceWidthRecord = record;
  faceWidthListeners.forEach((listener) => listener());
}

export function savePdMeasurementSession(record: PdMeasurementRecord) {
  currentPdRecord = record;
  unconsumedPdRecordId = record.recordId;
  pdListeners.forEach((listener) => listener());
}

export function replaceMeasurementSession({
  faceWidth,
  pd,
  feedbackSubmitted,
}: {
  faceWidth: FaceMeasurementRecord | null;
  pd: PdMeasurementRecord | null;
  feedbackSubmitted: boolean;
}) {
  currentFaceWidthRecord = faceWidth;
  currentPdRecord = pd;
  currentFeedbackSubmitted = feedbackSubmitted;
  unconsumedPdRecordId = null;
  faceWidthListeners.forEach((listener) => listener());
  pdListeners.forEach((listener) => listener());
  feedbackListeners.forEach((listener) => listener());
}

export function setMeasurementFeedbackSubmitted(submitted: boolean) {
  if (currentFeedbackSubmitted === submitted) return;
  currentFeedbackSubmitted = submitted;
  feedbackListeners.forEach((listener) => listener());
}

export function getMeasurementFeedbackSubmitted() {
  return currentFeedbackSubmitted;
}

export function readPdMeasurementSession() {
  return currentPdRecord;
}

export function consumePdMeasurementSession(recordId: string) {
  if (unconsumedPdRecordId !== recordId) return false;
  unconsumedPdRecordId = null;
  return true;
}

export function clearFaceMeasurementSession() {
  currentFaceWidthRecord = null;
  faceWidthListeners.forEach((listener) => listener());
}

export function clearPdMeasurementSession() {
  currentPdRecord = null;
  unconsumedPdRecordId = null;
  pdListeners.forEach((listener) => listener());
}

export function useFaceMeasurementSession() {
  return useSyncExternalStore(subscribeFaceWidth, getFaceWidthSnapshot, getServerSnapshot);
}

export function usePdMeasurementSession() {
  return useSyncExternalStore(subscribePd, getPdSnapshot, getServerSnapshot);
}

export function useMeasurementFeedbackSubmitted() {
  return useSyncExternalStore(subscribeFeedback, getFeedbackSnapshot, getServerFeedbackSnapshot);
}

export function useActiveFaceMeasurement() {
  return useFaceMeasurementSession();
}

export function useActivePdMeasurement() {
  return usePdMeasurementSession();
}
